import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/config/queries';
import { supabase } from '@/integrations/supabase/client';
import type { CreateHealthEventInput, HealthEventSummary } from '@/types/health-event';

const EVENT_WINDOW_DAYS = 14;
const EVENT_WINDOW_LIMIT = 50;

function eventWindowStart(): string {
  return new Date(Date.now() - EVENT_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
}

export function useHealthEvents(userId?: string) {
  const queryClient = useQueryClient();
  const queryKey = QUERY_KEYS.HEALTH_EVENTS(userId ?? 'anonymous');

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<HealthEventSummary[]> => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('health_events')
        .select(
          'id, user_id, event_type, custom_label, occurred_at, duration_minutes, source, created_at, updated_at',
        )
        .eq('user_id', userId)
        .gte('occurred_at', eventWindowStart())
        .lte('occurred_at', new Date().toISOString())
        .order('occurred_at', { ascending: false })
        .limit(EVENT_WINDOW_LIMIT);

      if (error) throw error;
      return (data ?? []) as HealthEventSummary[];
    },
    enabled: Boolean(userId),
    staleTime: 60_000,
    retry: 1,
  });

  const createEvent = useMutation({
    mutationFn: async (input: CreateHealthEventInput): Promise<HealthEventSummary> => {
      if (!userId) throw new Error('Authentication is required to record a health event');

      const durationMinutes = input.durationMinutes ?? null;
      if (durationMinutes !== null && (durationMinutes < 1 || durationMinutes > 720)) {
        throw new Error('Duration must be between 1 and 720 minutes');
      }
      const occurredAt = input.occurredAt ?? new Date().toISOString();
      const occurredAtMs = Date.parse(occurredAt);
      if (!Number.isFinite(occurredAtMs) || occurredAtMs > Date.now() + 5 * 60 * 1000) {
        throw new Error('Health events cannot be recorded in the future');
      }

      const { data, error } = await supabase
        .from('health_events')
        .insert({
          user_id: userId,
          event_type: input.eventType,
          occurred_at: occurredAt,
          duration_minutes: durationMinutes,
          note: input.note?.trim().slice(0, 280) || null,
          custom_label: input.customLabel?.trim().slice(0, 48) || null,
          source: 'manual',
        })
        .select(
          'id, user_id, event_type, custom_label, occurred_at, duration_minutes, source, created_at, updated_at',
        )
        .single();

      if (error) throw error;
      return data as HealthEventSummary;
    },
    onSuccess: (event) => {
      queryClient.setQueryData<HealthEventSummary[]>(queryKey, (current = []) => [
        event,
        ...current.filter((item) => item.id !== event.id),
      ]);
    },
  });

  const deleteEvent = useMutation({
    mutationFn: async (eventId: string) => {
      if (!userId) throw new Error('Authentication is required to delete a health event');

      const { error } = await supabase
        .from('health_events')
        .delete()
        .eq('id', eventId)
        .eq('user_id', userId);

      if (error) throw error;
      return eventId;
    },
    onSuccess: (eventId) => {
      queryClient.setQueryData<HealthEventSummary[]>(queryKey, (current = []) =>
        current.filter((event) => event.id !== eventId),
      );
    },
  });

  return {
    events: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    createEvent,
    deleteEvent,
  };
}
